// Reader links (PROTOCOL.md §7): a random review key per link, the capability derived from it (the relay keeps only its
// SHA-256), the link's form, and the associated data each sealed value carries. Shared by the reader page, the writer's
// app, the Worker and the crypto vectors. The reader page imports this file alone (no Automerge, no API client).
import { fromB64u, toB64u, utf8 } from './b64.ts';
import { hkdf, open, random, seal, sha256 } from './crypto.ts';

type Bytes = Uint8Array<ArrayBuffer>;

export const REVIEW_KEY_BYTES = 32;

/** A new review key: 32 random bytes, one per link. */
export const newReviewKey = (): Bytes => random(REVIEW_KEY_BYTES);

/** capability = HKDF(reviewKey, "writing-suite/review-capability/v1"). */
export const reviewCapability = (reviewKey: Uint8Array) => hkdf(reviewKey, 'writing-suite/review-capability/v1');

/** What the relay stores: b64u(SHA-256(capability)). */
export async function capabilityHash(capability: Uint8Array): Promise<string> {
  return toB64u(await sha256(capability));
}

/** The header a reader's requests carry. */
export const capabilityHeader = (capability: Uint8Array) => `Capability ${toB64u(capability)}`;

/** What each sealed value of a review authenticates: its review and its kind. */
export type ReviewSealKind = 'copy' | 'note' | 'progress';
export const reviewAAD = (reviewId: string, kind: ReviewSealKind) => `writing-suite/review/v1:${reviewId}:${kind}`;

export function sealReview(reviewKey: Uint8Array, reviewId: string, kind: ReviewSealKind, json: string, nonce?: Uint8Array): Promise<Bytes> {
  return seal(reviewKey, utf8(json), reviewAAD(reviewId, kind), nonce);
}

export async function openReview(reviewKey: Uint8Array, reviewId: string, kind: ReviewSealKind, sealed: Uint8Array): Promise<string> {
  return new TextDecoder('utf-8', { fatal: true, ignoreBOM: false }).decode(await open(reviewKey, sealed, reviewAAD(reviewId, kind)));
}

/** Review ids, note ids and reader ids are lowercase UUIDs (`IDs.make()`). */
export const REVIEW_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** `<readOrigin>/r/<reviewId>#<b64u(reviewKey)>`: the fragment never reaches a server. */
export function reviewLink(readOrigin: string, reviewId: string, reviewKey: Uint8Array): string {
  return `${readOrigin.replace(/\/$/, '')}/r/${reviewId}#${toB64u(reviewKey)}`;
}

/** The review id and key a link (or a reader page's location) carries, or null when it isn't one. */
export function parseReviewLink(pathname: string, hash: string): { reviewId: string; reviewKey: Bytes } | null {
  const m = /^\/r\/([0-9a-f-]{36})\/?$/.exec(pathname);
  if (!m || !REVIEW_ID.test(m[1])) return null;
  const encoded = hash.replace(/^#/, '');
  try {
    const reviewKey = fromB64u(encoded);
    return reviewKey.length === REVIEW_KEY_BYTES ? { reviewId: m[1], reviewKey } : null;
  } catch {
    return null;
  }
}
